import React, { useEffect, useRef } from 'react';
import { trackEvent } from '../analytics';

const MILESTONES = [25, 50, 75];

function TrackedVideo({ videoId, children, ...videoProps }) {
  const videoRef = useRef(null);
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const milestonesRef = useRef(new Set());

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    // A new videoId on the same instance is a new video — reset tracking
    // state or start/progress/complete events would never fire again.
    startedRef.current = false;
    completedRef.current = false;
    milestonesRef.current = new Set();

    const handlePlay = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      trackEvent('video_start', { video_id: videoId });
    };

    const handleTimeUpdate = () => {
      const duration = el.duration;
      if (!duration || !isFinite(duration) || duration <= 0) return;
      const percent = (el.currentTime / duration) * 100;
      for (const milestone of MILESTONES) {
        if (percent >= milestone && !milestonesRef.current.has(milestone)) {
          milestonesRef.current.add(milestone);
          trackEvent('video_progress', { video_id: videoId, percent: milestone });
        }
      }
    };

    const handleEnded = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      trackEvent('video_complete', { video_id: videoId });
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('ended', handleEnded);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('timeupdate', handleTimeUpdate);
      el.removeEventListener('ended', handleEnded);
    };
  }, [videoId]);

  return (
    <video ref={videoRef} {...videoProps}>
      {children}
    </video>
  );
}

export default TrackedVideo;
